<template>
    <div
        class="coupon-card"
        role="button"
        tabindex="0"
        :aria-label="t('dashboard-copyCode')"
        @click="copyCode"
        @keydown.enter.prevent="copyCode"
        @keydown.space.prevent="copyCode"
    >
        <div class="coupon-card-top">
            <span class="coupon-card-tag" v-lang="'dashboard-couponAvailable'"></span>

            <span class="coupon-card-copy" :class="{ 'is-copied': copied }">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <span>{{ copied ? t("dashboard-codeCopied") : coupon.code }}</span>
            </span>
        </div>

        <div class="coupon-card-text">
            <h3 class="coupon-card-title">{{ coupon.text || coupon.description }}</h3>
            <p class="coupon-card-meta" v-if="validUntil">{{ validUntil }}</p>
        </div>
    </div>
</template>

<script>
const COPIED_FEEDBACK_MS = 2000;

export default {
    name: "CouponCard",
    props: {
        coupon: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            copied: false,
            copiedTimer: null,
        };
    },
    computed: {
        validUntil() {
            const raw = this.coupon.expiration_date;
            if (!raw) {
                return "";
            }

            const parts = String(raw).split(" ")[0].split("-");
            if (parts.length !== 3) {
                return "";
            }

            return this.t("dashboard-couponValidUntil", {
                date: parts[2] + "/" + parts[1] + "/" + parts[0],
            });
        },
    },
    beforeDestroy() {
        if (this.copiedTimer) {
            clearTimeout(this.copiedTimer);
        }
    },
    methods: {
        copyCode() {
            this.writeToClipboard(this.coupon.code);

            this.copied = true;
            if (this.copiedTimer) {
                clearTimeout(this.copiedTimer);
            }
            this.copiedTimer = setTimeout(() => {
                this.copied = false;
            }, COPIED_FEEDBACK_MS);
        },
        writeToClipboard(text) {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text);
                return;
            }

            const field = document.createElement("textarea");
            field.value = text;
            field.setAttribute("readonly", "");
            field.style.position = "absolute";
            field.style.left = "-9999px";
            document.body.appendChild(field);
            field.select();

            try {
                document.execCommand("copy");
            } catch (e) {
            }

            document.body.removeChild(field);
        },
    },
};
</script>

<style lang="scss">
.coupon-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 1rem;
  background: #fcfcfd;
  border: 1px solid #17b26a;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.2s;

  &:hover {
    box-shadow: 0 0.25rem 0.75rem rgba(23, 178, 106, 0.15);
  }

  &:active {
    transform: scale(0.99);
  }

  &:focus-visible {
    outline: 2px solid #17b26a;
    outline-offset: 2px;
  }
}

.coupon-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.coupon-card-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem;
  border-radius: 0.5rem;
  background: #d3f8e0;
  color: #0b9055;
  font-size: 0.875rem;
  line-height: 1.3;
}

.coupon-card-copy {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fcfcfd;
  color: #1f2a37;
  font-size: 1rem;
  line-height: 1.2;
  transition: border-color 0.2s, color 0.2s;
}

.coupon-card-copy.is-copied {
  border-color: #17b26a;
  color: #0b9055;
}

.coupon-card-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.coupon-card-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3;
  color: #4d5761;
  overflow-wrap: anywhere;
}

.coupon-card-meta {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
  color: #4d5761;
}
</style>
